import { VideoEditor } from '../VideoEditor';
import { TimelineMode } from './TimelineMode';
import {
  AbstractElementCommand,
  AffectedElementOptions,
  RippleOptions,
} from './AbstractCommand';
import { Element } from '../models/Element';
import { TrackManager } from '../lib/TrackManager';

/**
 * Command to trim the start of an element (adjusts start time and duration)
 * Uses direct object references
 */
export class TrimElementStartCommand extends AbstractElementCommand {
  private newStartTime: number;
  private originalState: Record<string, any>;
  private trackManager: TrackManager;

  /**
   * Creates a new TrimElementStartCommand
   * @param editor The video editor instance
   * @param element The element to trim
   * @param newStartTime The new start time for the element (in seconds)
   */
  constructor(editor: VideoEditor, element: Element, newStartTime: number) {
    super(editor, element);

    // Get TrackManager from the editor instead of creating a new one
    this.trackManager = this.editor.getTrackManager();

    // Ensure time is never negative and apply frame snapping
    this.newStartTime = Math.max(0, this.snapTimeToFrame(newStartTime));

    // Capture full element state for undo
    this.originalState = this.captureElementState(this.sourceElement);

    // Store affected elements for undo in ripple mode
    if (this.getTimelineMode() === TimelineMode.RIPPLE) {
      this.storeAffectedElements({});
    }
  }

  execute(): boolean {
    // Use the trackManager instance with direct object reference
    const result = this.trackManager.trimElementStart(
      this.sourceElement,
      this.newStartTime,
      this.getTimelineMode(), // Pass the mode explicitly
    );

    if (result) {
      // Recalculate timeline duration after successful trim
      this.recalculateTimeline();
    }

    return result;
  }

  undo(): boolean {
    // Restore the complete original element state
    this.restoreElementState(
      this.sourceElement,
      this.originalState,
    );

    // Restore affected elements if in ripple mode
    this.restoreAffectedElements();

    // Recalculate timeline duration
    this.recalculateTimeline();

    return true;
  }

  getDescription(): string {
    return `Trim start of element ${this.sourceElement.id} to ${this.newStartTime}s`;
  }
}
